/**
 * Mobile Team Box Score
 * Full box score page with date navigation and team selection
 * Uses the same UI pattern as dashboard but adds controls
 */

import React, { useState, useEffect } from 'react';
import { dynastyTheme, dynastyTileStyle, withAlpha } from '../../../services/colorService';
import { mobileModalStyle } from '../../../services/mobileStyleService';
import { leaguesAPI } from '../../../services/apiService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { fetchBoxScore, fetchAvailableDates, fetchBoxScoreRankings } from '../../../services/boxScoreApi';
import BoxScoreCategories from '../components/BoxScoreCategories';
import MobileSectionHeader from '../components/MobileSectionHeader';
import MobileTableSectionHeader from '../components/MobileTableSectionHeader';
import { MobilePlayerModal } from '../components/player';
import { MobileDynastyTable } from '../components/MobileDynastyTable';
// Bible § 14 stat-column rule: box-score columns derived from
// league_settings.scoring_categories + a fixed context prepend, adapted to the
// MobileDynastyTable column shape. Shared with the League-Home tile
// (MobileBoxScoreTile) + MobileTeamHome — single source of truth.
import { buildMobileBoxScoreColumns } from '../utils/boxScoreColumns';

const MobileTeamBoxScore = ({ leagueId, initialTeamId = null }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [league, setLeague] = useState(null);  // for league_settings.scoring_categories (Bible § 14)

  // Team data
  const [userTeamId, setUserTeamId] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  
  // Box score data
  const [currentDate, setCurrentDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [boxScoreRankings, setBoxScoreRankings] = useState(null);
  const [activeTab, setActiveTab] = useState('hitting');
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Player modal state
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  // Load teams on mount
  useEffect(() => {
    if (leagueId) {
      loadAllTeams();
      // Fetch league for league_settings.scoring_categories (Bible § 14)
      leaguesAPI.getLeagueDetails(leagueId)
        .then((res) => {
          if (res?.success && res.league) setLeague(res.league);
        })
        .catch(() => {});
    }
  }, [leagueId]);

  // The banner team-selector (parent MobilePageHeader inlineDropdown) drives which
  // team's box we view — keep our viewed team in sync with the parent's selection.
  useEffect(() => {
    if (initialTeamId) setSelectedTeamId(initialTeamId);
  }, [initialTeamId]);

  // Load available dates when team changes
  useEffect(() => {
    if (leagueId && selectedTeamId) {
      loadAvailableDates();
    }
  }, [leagueId, selectedTeamId]);

  // Load box score when date or team changes
  useEffect(() => {
    if (leagueId && selectedTeamId && currentDate) {
      loadBoxScore();
    }
  }, [leagueId, selectedTeamId, currentDate]);

  const loadAllTeams = async () => {
    try {
      setLoading(true);
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      
      if (response.success && response.teams) {
        const teamList = response.teams.map(team => ({
          team_id: team.team_id,
          team_name: team.team_name || 'Unnamed Team',
          is_user_team: team.is_user_team || false,
        }));
        
        setAllTeams(teamList);
        
        // Find user's team and set as default
        const userTeam = teamList.find(t => t.is_user_team);
        if (userTeam) {
          setUserTeamId(userTeam.team_id);
        }
        // Use initialTeamId if provided, otherwise default to user's team
        setSelectedTeamId(initialTeamId || userTeam?.team_id || null);
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDates = async () => {
    try {
      // Don't pass season_year — let the backend use the league's current_season
      const response = await fetchAvailableDates(leagueId, selectedTeamId);
      
      if (response.success && response.dates) {
        const sortedDates = [...response.dates].sort((a, b) => b.localeCompare(a));
        setAvailableDates(sortedDates);
        
        // Set most recent date as default
        if (sortedDates.length > 0 && !currentDate) {
          setCurrentDate(sortedDates[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load dates:', err);
    }
  };

  const loadBoxScore = async () => {
    try {
      setLoading(true);
      
      // Fetch box score
      const response = await fetchBoxScore(leagueId, selectedTeamId, currentDate);
      
      if (response.success && response.box_score) {
        setBoxScoreData({
          hitting: response.box_score.hitting || [],
          pitching: response.box_score.pitching || [],
          team_totals: response.box_score.team_totals || null
        });
      }
      
      // Fetch rankings - store full response (includes your_ranks AND all_rankings)
      try {
        const rankingsResponse = await fetchBoxScoreRankings(leagueId, selectedTeamId, currentDate);
        if (rankingsResponse.success && rankingsResponse.your_ranks) {
          // Store the full response (includes your_ranks AND all_rankings)
          setBoxScoreRankings(rankingsResponse);
        }
      } catch (rankingsError) {
        console.error('Rankings error:', rankingsError);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to load box score:', err);
      setError('Failed to load box score');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to previous date
  const handlePreviousDate = () => {
    const currentIndex = availableDates.indexOf(currentDate);
    if (currentIndex < availableDates.length - 1) {
      setCurrentDate(availableDates[currentIndex + 1]);
    }
  };

  // Navigate to next date
  const handleNextDate = () => {
    const currentIndex = availableDates.indexOf(currentDate);
    if (currentIndex > 0) {
      setCurrentDate(availableDates[currentIndex - 1]);
    }
  };

  // Handle date picker selection
  const handleDatePickerSelect = (selectedDate) => {
    if (availableDates.includes(selectedDate)) {
      setCurrentDate(selectedDate);
      setShowDatePicker(false);
    } else {
      alert('No box score available for this date');
    }
  };

  // Consume pre-calculated team totals from backend BoxScoreService
  const backendHitting = boxScoreData?.team_totals?.hitting || {};
  const backendPitching = boxScoreData?.team_totals?.pitching || {};

  const hittingTotals = {
    ab: backendHitting.at_bats || 0,
    r: backendHitting.runs || 0,
    h: backendHitting.hits || 0,
    hr: backendHitting.home_runs || 0,
    rbi: backendHitting.rbi || 0,
    sb: backendHitting.stolen_bases || 0,
    bb: backendHitting.walks || 0,
    so: backendHitting.strikeouts || 0,
    avg: formatBattingAvg(backendHitting.avg) || '.000',
    // DD-STAT-CONSOLIDATION C3: emit OBP/SLG totals (were omitted → '-' when the league
    // scores them). Backend team_totals.hitting carries correct component-recomputed rates.
    obp: formatBattingAvg(backendHitting.obp) || '.000',
    slg: formatBattingAvg(backendHitting.slg) || '.000',
    ops: formatBattingAvg(backendHitting.ops) || '.000'
  };

  const pitchingTotals = {
    w: backendPitching.wins || 0,
    l: backendPitching.losses || 0,
    sv: backendPitching.saves || 0,
    ip: backendPitching.innings_pitched != null ? backendPitching.innings_pitched.toFixed(1) : '0.0',
    h: backendPitching.hits_allowed || 0,
    er: backendPitching.earned_runs || 0,
    bb: backendPitching.walks_allowed || 0,
    so: backendPitching.strikeouts_pitched || 0,
    qs: backendPitching.quality_starts || 0,
    era: formatERA(backendPitching.era) || '0.00',
    whip: formatBattingAvg(backendPitching.whip) || '.000'
  };

  if (loading && !boxScoreData) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: dynastyTheme.tokens.colors.neutral[400]
      }}>
        <div style={{
          width: '30px',
          height: '30px',
          border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 10px'
        }}></div>
        Loading box score...
      </div>
    );
  }

  if (error && !boxScoreData) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: dynastyTheme.tokens.colors.error + '20',
        border: `1px solid ${dynastyTheme.tokens.colors.error}`,
        borderRadius: '8px',
        color: dynastyTheme.tokens.colors.error
      }}>
        {error}
      </div>
    );
  }

  const currentDateIndex = availableDates.indexOf(currentDate);
  const canGoPrevious = currentDateIndex < availableDates.length - 1;
  const canGoNext = currentDateIndex > 0;

  return (
    <div>
      {/* Team selector now lives in the banner (MobilePageHeader inlineDropdown,
          driven by the parent) and the date-nav is inline on the rankings header
          below — the old bordered switcher/day-nav strip is gone. */}

      {/* Daily Category Rankings — flush left-rule header + scroll hint */}
      {boxScoreData && (boxScoreData.hitting.length > 0 || boxScoreData.pitching.length > 0) && (
        <div style={{ padding: '0 12px' }}>
          <MobileSectionHeader
            title="Daily Category Rankings"
            hint="▸ scroll"
            right={(
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={handlePreviousDate}
                  disabled={!canGoPrevious}
                  aria-label="Previous date"
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: '13px', cursor: canGoPrevious ? 'pointer' : 'default', color: canGoPrevious ? dynastyTheme.tokens.colors.muted : 'transparent', pointerEvents: canGoPrevious ? 'auto' : 'none' }}
                >◀</button>
                <button
                  onClick={() => setShowDatePicker(true)}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: '11px', fontWeight: '700', color: dynastyTheme.tokens.colors.white, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}
                >
                  {currentDate ? new Date(currentDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                </button>
                <button
                  onClick={handleNextDate}
                  disabled={!canGoNext}
                  aria-label="Next date"
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: '13px', cursor: canGoNext ? 'pointer' : 'default', color: canGoNext ? dynastyTheme.tokens.colors.mutedLight : 'transparent', pointerEvents: canGoNext ? 'auto' : 'none' }}
                >▶</button>
              </span>
            )}
          />

          <BoxScoreCategories
            league={league}
            rankings={boxScoreRankings?.your_ranks || null}
            hittingTotals={hittingTotals}
            pitchingTotals={pitchingTotals}
            allRankings={boxScoreRankings?.all_rankings || null}
            yourTeamId={selectedTeamId}
            statsDate={boxScoreRankings?.stats_date || currentDate || ''}
            weeklyHistory={boxScoreRankings?.your_team_weekly_history || null}
            wow={boxScoreRankings?.your_team_wow || null}
            weekly={boxScoreRankings?.your_team_weekly || null}
            lastWeek={boxScoreRankings?.your_team_last_week || null}
            season={boxScoreRankings?.your_team_season || null}
            weekMeta={boxScoreRankings?.week_meta || null}
          />
        </div>
      )}

      {/* Box Score Tables */}
      {boxScoreData && (() => {
        // Bible § 14: derive box-score columns from league scoring + context
        // prepend, adapted to the MobileDynastyTable column shape. Full page
        // includes the (non-frozen) Tm column.
        const rows = activeTab === 'hitting' ? boxScoreData.hitting : boxScoreData.pitching;
        const columns = buildMobileBoxScoreColumns({
          scoringCategories: league?.league_settings?.scoring_categories,
          activeTab,
          includeTeamColumn: true,
          onPlayerClick: (id) => {
            setSelectedPlayerId(id.toString());
            setIsPlayerModalOpen(true);
          },
        });
        const currentTotals = activeTab === 'hitting' ? hittingTotals : pitchingTotals;
        return (
        // BoxMini.dc.html header: green (success) bar + gold "Box Score · <date>"
        // label + INLINE right-aligned Hitters/Pitchers tabs — the shared
        // MobileTableSectionHeader, same as the dashboard.
        <div style={{ padding: '0 12px' }}>
          <MobileTableSectionHeader
            label={`Box Score${currentDate ? ` · ${new Date(currentDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}`}
            color={dynastyTheme.tokens.colors.primary}
            barColor={dynastyTheme.tokens.colors.primary}
            tabs={{
              active: activeTab,
              onSelect: setActiveTab,
              options: [
                { key: 'hitting', label: `Hitters ${boxScoreData.hitting.length}` },
                { key: 'pitching', label: `Pitchers ${boxScoreData.pitching.length}` },
              ],
            }}
          />

          {/* Table — shared MobileDynastyTable primitive, flush (house zebra) */}
          <div>
            <MobileDynastyTable
              data={rows}
              columns={columns}
              keyField="player_id"
              tableType="player-list"
              showTotals={rows.length > 0}
              totalsRow={currentTotals}
              rowDivider={`1px solid ${withAlpha(dynastyTheme.tokens.colors.goldBase, 0.5)}`}
            />
          </div>

          {rows.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: dynastyTheme.tokens.colors.neutral[500]
            }}>
              No {activeTab} data for this date
            </div>
          )}
        </div>
        );
      })()}

      {/* Player Modal */}
      <MobilePlayerModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerModalOpen}
        onClose={() => {
          setIsPlayerModalOpen(false);
          setSelectedPlayerId(null);
        }}
      />

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: mobileModalStyle.backdrop,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowDatePicker(false)}
        >
          <div
            style={{
              backgroundColor: dynastyTheme.tokens.colors.neutral[800],
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '400px',
              width: '100%',
              maxHeight: '70vh',
              overflowY: 'auto',
              border: `2px solid ${dynastyTheme.tokens.colors.primary}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: dynastyTheme.tokens.colors.white,
                margin: 0
              }}>
                📅 Select Date
              </h3>
              <button
                onClick={() => setShowDatePicker(false)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: dynastyTheme.tokens.colors.neutral[700],
                  color: dynastyTheme.tokens.colors.neutral[400],
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            <div>
              {availableDates.map((date, index) => {
                const isSelected = date === currentDate;
                const dateObj = new Date(date + 'T00:00:00');
                const formattedDate = dateObj.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });

                return (
                  <button
                    key={date}
                    onClick={() => handleDatePickerSelect(date)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: isSelected 
                        ? dynastyTheme.tokens.colors.primary 
                        : dynastyTheme.tokens.colors.neutral[750],
                      color: isSelected ? dynastyTheme.tokens.colors.black : dynastyTheme.tokens.colors.white,
                      border: isSelected
                        ? `2px solid ${dynastyTheme.tokens.colors.primary}`
                        : `1px solid ${dynastyTheme.tokens.colors.neutral[600]}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: isSelected ? '700' : '500',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>{formattedDate}</span>
                    {isSelected && <span>✓</span>}
                  </button>
                );
              })}

              {availableDates.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: dynastyTheme.tokens.colors.neutral[500],
                  fontSize: '14px'
                }}>
                  No dates available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileTeamBoxScore;
